<template>
  <v-dialog
    max-width="650px"
    :model-value="show"
    @update:model-value="!state.loading && $emit('update:show', $event)"
    :fullscreen="$vuetify.display.xs"
  >
    <v-card>
      <v-card-title>
        <div class="d-flex justify-space-between">
          <typography weight="600" size="1.25rem">Account Settings</typography>
        </div>
      </v-card-title>
      <v-divider class="m-0"></v-divider>
      <v-card-item>
        <div class="form-items">
          <div class="form-item">
            <div class="item-label">
              <Typography weight="600">First Name</Typography>
            </div>
            <div class="item-field">
              <v-text-field class="custom-input" v-model="state.firstname"></v-text-field>
            </div>
          </div>
          <div class="form-item">
            <div class="item-label">
              <Typography weight="600">Last Name</Typography>
            </div>
            <div class="item-field">
              <v-text-field class="custom-input" v-model="state.lastname"></v-text-field>
            </div>
          </div>
          <div class="form-item">
            <div class="item-label">
              <Typography weight="600">External ID</Typography>
            </div>
            <div class="item-field">
              <v-text-field disabled class="custom-input" v-model="state.externalID"></v-text-field>
            </div>
          </div>
          <div class="form-item">
            <div class="item-label">
              <Typography weight="600">Email Address</Typography>
            </div>
            <div class="item-field">
              <v-text-field class="custom-input" v-model="state.email"></v-text-field>
            </div>
          </div>
          <div class="form-items switch">
            <!-- Google Integrations -->
            <div class="form-item switch">
              <div class="d-flex align-center gap-3">
                <div class="icons8-google-calendar" />
                <typography weight="600">Google Calendar</typography>
                <v-switch
                  density="compact"
                  class="custom-input"
                  color="primary"
                  :model-value="state.allowGCalendar"
                  @update:model-value="updateGoogle($event as any)"
                  :loading="state.loadingGoogleCalendar"
                ></v-switch>
              </div>
            </div>
            <div class="form-item switch">
              <div class="d-flex align-center gap-3" tabindex="1">
                <div class="icons8-google-meet" />
                <typography weight="600">Google Meet</typography>
                <v-switch
                  density="compact"
                  class="custom-input"
                  color="primary"
                  :model-value="state.allowGoogleMeet"
                  @update:model-value="updateGoogleMeet($event as any)"
                  :loading="state.loadingGoogleMeet"
                  :disabled="!state.allowGCalendar"
                >
                </v-switch>
                <v-tooltip
                  v-if="!state.allowGCalendar"
                  activator="parent"
                  location="bottom"
                  class="cutom-tooltip"
                  >Enable google calendar to enable google meet</v-tooltip
                >
              </div>
            </div>
            <!-- Microsoft Integrations -->
            <div class="form-item switch">
              <div class="d-flex align-center gap-3">
                <div class="icons8-outlook-calendar" />
                <typography weight="600">Connect Microsoft Outlook</typography>
                <v-switch
                  density="compact"
                  class="custom-input"
                  color="primary"
                  :model-value="state.allowOutlook"
                  @update:model-value="updateOutlook($event as any)"
                  :loading="state.loadingOutlook"
                >
                  <v-tooltip activator="parent" location="bottom" class="custom-tooltip">
                    Enables PTC Wizard integration with Outlook Calendar
                  </v-tooltip>
                </v-switch>
              </div>
            </div>

            <div class="form-item switch">
              <div class="d-flex align-center gap-3" tabindex="1">
                <div class="icons8-microsoft-teams" />

                <typography weight="600">Microsoft Teams</typography>
                <v-switch
                  density="compact"
                  class="custom-input"
                  color="primary"
                  :model-value="state.allowAzureTeams"
                  @update:model-value="updateTeams($event as any)"
                  :loading="state.loadingAzureTeams"
                  :disabled="!state.allowOutlook"
                >
                </v-switch>
                <v-tooltip
                  v-if="!state.allowOutlook"
                  activator="parent"
                  location="bottom"
                  class="custom-tooltip"
                  >Enable MS Outlook to enable MS Teams</v-tooltip
                >
              </div>
            </div>
            <!-- Zoom Integration -->
            <div class="form-item switch">
              <div class="d-flex align-center gap-3">
                <div class="icons8-zoom" />

                <typography weight="600">Allow Zoom Meetings</typography>
                <v-switch
                  density="compact"
                  class="custom-input"
                  color="primary"
                  :model-value="state.allowZoom"
                  @update:model-value="updateZoom()"
                  :loading="state.loadingZoom"
                ></v-switch>
              </div>
            </div>
          </div>
        </div>
      </v-card-item>
      <v-divider class="m-0"></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :loading="state.loading"
          @click="submit"
          color="primary"
          variant="flat"
          style="text-transform: none"
          >Save Changes</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useAuthenticationStore } from "@/stores/authentication";
import { FetchService } from "@/services/fetch.service";
import Typography from "@/components/typography.vue";
import { reactive } from "vue";
import { watch } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import { integrationWindow } from "@/utils/integrationWindow";

const props = defineProps<{
  show: boolean;
}>();
defineEmits<{ (event: "update:show", val: boolean): void }>();

const authStore = useAuthenticationStore();
const userToast = useAlertsStore();

const authenticationStore = useAuthenticationStore();
const state = reactive({
  firstname: authenticationStore.me.teacher_fname,
  lastname: authenticationStore.me.teacher_lname,
  externalID: authenticationStore.me.external_teacher_id,
  email: authenticationStore.me.email,
  allowZoom: !!authenticationStore.me.zoom_connected,
  allowGCalendar: !!authenticationStore.me.google_calendar_connected,
  allowOutlook: !!authenticationStore.me.azure_connected,
  allowAzureTeams: !!authenticationStore.me.azure_connected && !!authenticationStore.me.azure_teams,
  allowGoogleMeet:
    !!authenticationStore.me.google_meet && !!authenticationStore.me.google_calendar_connected,
  loading: false,
  loadingGoogleCalendar: false,
  loadingGoogleMeet: false,
  loadingZoom: false,
  loadingOutlook: false,
  loadingAzureTeams: false
});
watch([() => authenticationStore.me, () => props.show], () => {
  Object.assign(state, {
    firstname: authenticationStore.me.teacher_fname,
    lastname: authenticationStore.me.teacher_lname,
    externalID: authenticationStore.me.external_teacher_id,
    email: authenticationStore.me.email,
    allowGCalendar: !!authenticationStore.me.google_calendar_connected,
    allowZoom: !!authenticationStore.me.zoom_connected,
    allowOutlook: !!authenticationStore.me.azure_connected,
    allowAzureTeams:
      !!authenticationStore.me.azure_connected && !!authenticationStore.me.azure_teams,
    allowGoogleMeet:
      !!authenticationStore.me.google_meet && !!authenticationStore.me.google_calendar_connected
  });
});

const submit = async () => {
  state.loading = true;

  const res = await FetchService.fetchData(`/teachers/${authStore.me.uid}`, "put", {
    teacher_fname: state.firstname,
    teacher_lname: state.lastname,
    email: state.email,
    allow_zoom: state.allowZoom,
    allow_google_meet: state.allowGCalendar
  });
  console.log(res);
  state.loading = false;
  if (res?.teacher) {
    Object.assign(authStore.me, res.teacher);
  }
};

const updateZoom = async () => {
  state.loadingZoom = true;

  if (!state.allowZoom) {
    const url = await FetchService.fetchData("/zoom/url", "get");

    const res = await integrationWindow(url, "Zoom integration enabled successfully");

    if (res.close || res.error || res.success) {
      state.loadingZoom = false;
    }
    if (res.success) {
      await authStore.fetchProfile();
      state.allowZoom = true;
      authenticationStore.me.zoom_connected = 1;
    }
  } else {
    // disable zoom api call

    state.allowZoom = false;
    const res = await FetchService.fetchData("/zoom", "delete");
    if (res?.success) {
      state.allowZoom = false;
      authenticationStore.me.zoom_connected = 0;
      userToast.addAlert("success", "Zoom integration disabled successfully", 7);
    } else {
      state.allowZoom = true;
      userToast.addAlert("error", res?.error || "Failed to disable Zoom integration", 7);
    }
    state.loadingZoom = false;
  }
};

const updateGoogle = async (val: boolean) => {
  state.loadingGoogleCalendar = true;
  if (val) {
    const url = await FetchService.fetchData("/googlecalendar/url", "get");

    const res = await integrationWindow(url, "Google Calendar integration enabled successfully");

    if (res.close || res.error || res.success) {
      state.loadingGoogleCalendar = false;
    }
    if (res.success) {
      console.log("google calendar connected...");
      await authStore.fetchProfile();
      state.allowGCalendar = true;
      authenticationStore.me.google_calendar_connected = true;
    }
  } else {
    // disable google calendar api call

    state.allowGCalendar = false;
    const res = await FetchService.fetchData("/googlecalendar", "delete");
    if (res?.success) {
      state.allowGCalendar = false;
      authenticationStore.me.google_calendar_connected = false;
      authenticationStore.me.google_meet = false;
      state.allowGoogleMeet = false;
      userToast.addAlert("success", "Google Calendar integration disabled successfully", 7);
    } else {
      state.allowGCalendar = true;
      userToast.addAlert("error", res?.error || "Failed to disable Google Calendar integration", 7);
    }
    state.loadingGoogleCalendar = false;
  }
};

const updateOutlook = async (val: boolean) => {
  state.loadingOutlook = true;
  if (val) {
    const url = await FetchService.fetchData("/outlook/url", "get");

    const res = await integrationWindow(
      url,
      "Microsoft Outlook Calendar integration enabled successfully"
    );

    if (res.close || res.error || res.success) {
      state.loadingOutlook = false;
    }
    if (res.success) {
      state.allowOutlook = true;
      authenticationStore.me.azure_connected = true;
    }
  } else {
    // disable outlook calendar api call

    state.allowOutlook = false;
    const res = await FetchService.fetchData("/outlook", "delete");
    if (res?.success) {
      state.allowOutlook = !!res.teacher?.azure_connected;
      authenticationStore.me.azure_connected = !!res.teacher?.azure_connected;
      state.allowAzureTeams = !!res.teacher?.azure_teams;
      authenticationStore.me.azure_teams = !!res.teacher?.azure_teams;
      userToast.addAlert("success", "Outlook Calendar integration disabled successfully", 7);
    } else {
      state.allowOutlook = true;
      userToast.addAlert(
        "error",
        res?.error || "Failed to disable Outlook Calendar integration",
        7
      );
    }
    state.loadingOutlook = false;
  }
};

const updateGoogleMeet = async (val: boolean) => {
  state.loadingGoogleMeet = true;
  state.allowGoogleMeet = false;

  const res = await FetchService.fetchData(`/teachers/${authStore.me.uid}`, "put", {
    teacher_fname: authenticationStore.me.teacher_fname,
    teacher_lname: authenticationStore.me.teacher_lname,
    email: authenticationStore.me.email,
    google_meet: val
  });
  state.loadingGoogleMeet = false;
  if (res?.teacher) {
    Object.assign(authStore.me, res.teacher);
    state.allowGoogleMeet = !!authStore.me.google_meet;
  } else state.allowGoogleMeet = !val;
};

const updateTeams = async (val: boolean) => {
  state.loadingAzureTeams = true;
  state.allowAzureTeams = false;

  const res = await FetchService.fetchData(`/teachers/${authStore.me.uid}`, "put", {
    azure_teams: val
  });
  state.loadingAzureTeams = false;
  if (res?.teacher) {
    Object.assign(authStore.me, res.teacher);
    state.allowAzureTeams = !!authStore.me.azure_teams;
  } else state.allowAzureTeams = !val;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.icons8-microsoft-teams {
  display: inline-block;
  width: 30px;
  height: 30px;
  background: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjBweCIgeT0iMHB4IiB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCI+CjxwYXRoIGZpbGw9IiM1YzZiYzAiIGQ9Ik00MS41IDEzQTMuNSAzLjUgMCAxIDAgNDEuNSAyMCAzLjUgMy41IDAgMSAwIDQxLjUgMTN6TTQgNDBsMjMgNFY0TDQgOFY0MHoiPjwvcGF0aD48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMjEgMTYuMjdMMjEgMTkgMTcuMDEgMTkuMTggMTYuOTkgMzEuMDQgMTQuMDEgMzAuOTUgMTQuMDEgMTkuMjkgMTAgMTkuNDUgMTAgMTYuOTR6Ij48L3BhdGg+PHBhdGggZmlsbD0iIzVjNmJjMCIgZD0iTTM2IDE0YzAgMi4yMS0xLjc5IDQtNCA0LTEuMiAwLTIuMjctLjUzLTMtMS4zNnYtNS4yOGMuNzMtLjgzIDEuOC0xLjM2IDMtMS4zNkMzNC4yMSAxMCAzNiAxMS43OSAzNiAxNHpNMzggMjN2MTFjMCAwIDEuNTY3IDAgMy41IDAgMS43NjIgMCAzLjIwNS0xLjMwNiAzLjQ1LTNINDV2LThIMzh6TTI5IDIwdjE3YzAgMCAxLjU2NyAwIDMuNSAwIDEuNzYyIDAgMy4yMDUtMS4zMDYgMy40NS0zSDM2VjIwSDI5eiI+PC9wYXRoPgo8L3N2Zz4=")
    50% 50% no-repeat;
  background-size: 100%;
}
.icons8-zoom {
  display: inline-block;
  width: 30px;
  height: 30px;
  background: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjBweCIgeT0iMHB4IiB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCI+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMjQiIHI9IjIwIiBmaWxsPSIjMjE5NmYzIj48L2NpcmNsZT48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMjksMzFIMTRjLTEuNjU3LDAtMy0xLjM0My0zLTNWMTdoMTVjMS42NTcsMCwzLDEuMzQzLDMsM1YzMXoiPjwvcGF0aD48cG9seWdvbiBmaWxsPSIjZmZmIiBwb2ludHM9IjM3LDMxIDMxLDI3IDMxLDIxIDM3LDE3Ij48L3BvbHlnb24+Cjwvc3ZnPg==")
    50% 50% no-repeat;
  background-size: 100%;
}
.icons8-google-meet {
  display: inline-block;
  width: 30px;
  height: 30px;
  background: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjBweCIgeT0iMHB4IiB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCI+CjxyZWN0IHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgeD0iMTIiIHk9IjE2IiBmaWxsPSIjZmZmIiB0cmFuc2Zvcm09InJvdGF0ZSgtOTAgMjAgMjQpIj48L3JlY3Q+PHBvbHlnb24gZmlsbD0iIzFlODhlNSIgcG9pbnRzPSIzLDE3IDMsMzEgOCwzMiAxMywzMSAxMywxNyA4LDE2Ij48L3BvbHlnb24+PHBhdGggZmlsbD0iIzRjYWY1MCIgZD0iTTM3LDI0djE0YzAsMS42NTctMS4zNDMsMy0zLDNIMTNsLTEtNWwxLTVoMTR2LTdsNS0xTDM3LDI0eiI+PC9wYXRoPjxwYXRoIGZpbGw9IiNmYmMwMmQiIGQ9Ik0zNywxMHYxNEgyN3YtN0gxM2wtMS01bDEtNWgyMUMzNS42NTcsNywzNyw4LjM0MywzNywxMHoiPjwvcGF0aD48cGF0aCBmaWxsPSIjMTU2NWMwIiBkPSJNMTMsMzF2MTBINmMtMS42NTcsMC0zLTEuMzQzLTMtM3YtN0gxM3oiPjwvcGF0aD48cG9seWdvbiBmaWxsPSIjZTUzOTM1IiBwb2ludHM9IjEzLDcgMTMsMTcgMywxNyI+PC9wb2x5Z29uPjxwb2x5Z29uIGZpbGw9IiMyZTdkMzIiIHBvaW50cz0iMzgsMjQgMzcsMzIuNDUgMjcsMjQgMzcsMTUuNTUiPjwvcG9seWdvbj48cGF0aCBmaWxsPSIjNGNhZjUwIiBkPSJNNDYsMTAuMTF2MjcuNzhjMCwwLjg0LTAuOTgsMS4zMS0xLjYzLDAuNzhMMzcsMzIuNDV2LTE2LjlsNy4zNy02LjIyQzQ1LjAyLDguOCw0Niw5LjI3LDQ2LDEwLjExeiI+PC9wYXRoPgo8L3N2Zz4=")
    50% 50% no-repeat;
  background-size: 100%;
}
.icons8-google-calendar {
  display: inline-block;
  width: 30px;
  height: 30px;
  background: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHg9IjBweCIgeT0iMHB4IiB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCA0OCA0OCI+CjxyZWN0IHdpZHRoPSIyMiIgaGVpZ2h0PSIyMiIgeD0iMTMiIHk9IjEzIiBmaWxsPSIjZmZmIj48L3JlY3Q+PHBvbHlnb24gZmlsbD0iIzFlODhlNSIgcG9pbnRzPSIyNS42OCwyMC45MiAyNi42ODgsMjIuMzYgMjguMjcyLDIxLjIwOCAyOC4yNzIsMjkuNTYgMzAsMjkuNTYgMzAsMTguNjE2IDI4LjU2LDE4LjYxNiI+PC9wb2x5Z29uPjxwYXRoIGZpbGw9IiMxZTg4ZTUiIGQ9Ik0yMi45NDMsMjMuNzQ1YzAuNjI1LTAuNTc0LDEuMDEzLTEuMzcsMS4wMTMtMi4yNDljMC0xLjc0Ny0xLjUzMy0zLjE2OC0zLjQxNy0zLjE2OCBjLTEuNjAyLDAtMi45NzIsMS4wMDktMy4zMywyLjQ1M2wxLjY1NywwLjQyMWMwLjE2NS0wLjY2NCwwLjg2OC0xLjE0NiwxLjY3My0xLjE0NmMwLjk0MiwwLDEuNzA5LDAuNjQ2LDEuNzA5LDEuNDQgYzAsMC43OTQtMC43NjcsMS40NC0xLjcwOSwxLjQ0aC0wLjk5N3YxLjcyOGgwLjk5N2MxLjA4MSwwLDEuOTkzLDAuNzUxLDEuOTkzLDEuNjRjMCwwLjkwNC0wLjg2NiwxLjY0LTEuOTMxLDEuNjQgYy0wLjk2MiwwLTEuNzg0LTAuNjEtMS45MTQtMS40MThMMTcsMjYuODAyYzAuMjYyLDEuNjM2LDEuODEsMi44NywzLjYsMi44N2MyLjAwNywwLDMuNjQtMS41MTEsMy42NC0zLjM2OCBDMjQuMjQsMjUuMjgxLDIzLjczNiwyNC4zNjMsMjIuOTQzLDIzLjc0NXoiPjwvcGF0aD48cG9seWdvbiBmaWxsPSIjZmJjMDJkIiBwb2ludHM9IjM0LDQyIDE0LDQyIDEzLDM4IDE0LDM0IDM0LDM0IDM1LDM4Ij48L3BvbHlnb24+PHBvbHlnb24gZmlsbD0iIzRjYWY1MCIgcG9pbnRzPSIzOCwzNSA0MiwzNCA0MiwxNCAzOCwxMyAzNCwxNCAzNCwzNCI+PC9wb2x5Z29uPjxwYXRoIGZpbGw9IiMxZTg4ZTUiIGQ9Ik0zNCwxNGwxLTRsLTEtNEg5QzcuMzQzLDYsNiw3LjM0Myw2LDl2MjVsNCwxbDQtMVYxNEgzNHoiPjwvcGF0aD48cG9seWdvbiBmaWxsPSIjZTUzOTM1IiBwb2ludHM9IjM0LDM0IDM0LDQyIDQyLDM0Ij48L3BvbHlnb24+PHBhdGggZmlsbD0iIzE1NjVjMCIgZD0iTTM5LDZoLTV2OGg4VjlDNDIsNy4zNDMsNDAuNjU3LDYsMzksNnoiPjwvcGF0aD48cGF0aCBmaWxsPSIjMTU2NWMwIiBkPSJNOSw0Mmg1di04SDZ2NUM2LDQwLjY1Nyw3LjM0Myw0Miw5LDQyeiI+PC9wYXRoPgo8L3N2Zz4=")
    50% 50% no-repeat;
  background-size: 100%;
}
.icons8-outlook-calendar {
  display: inline-block;
  width: 30px;
  height: 30px;
  background: url("data:image/svg+xml;base64,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")
    50% 50% no-repeat;
  background-size: 100%;
}
.form-items {
  display: flex;
  width: 100%;
  gap: 20px;
  flex-wrap: wrap;

  &.switch {
    gap: 0 20px;
  }
  .form-item {
    flex: 1;
    min-width: 200px;

    &.switch {
      min-width: 220px;
    }
  }
}
</style>
